import { useEffect, useState } from "react";
import { styled } from "styled-components";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiExternalLink } from "react-icons/fi";
import { FaGithubAlt } from "react-icons/fa";

import HomeHeading from "../../components/HomeHeading";
import curious from "../../assets/docs/curious.json";

export default function CuriousDetails() {
  const [data, setData] = useState(null);
  const [architectureLoaded, setArchitectureLoaded] = useState(false);

  const { slug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const found = curious.find(
      (item) =>
        item.slug?.toLowerCase() === slug?.toLowerCase() ||
        item.nav?.toLowerCase() === slug?.toLowerCase(),
    );

    if (!found) {
      navigate("/");
      return;
    }

    setData(found);
    setArchitectureLoaded(false);
  }, [navigate, slug]);

  if (!data) {
    return null;
  }

  const links = [
    {
      label: "GitHub",
      href: data.links?.github,
      icon: <FaGithubAlt />,
    },
  ].filter((item) => item.href);

  return (
    <Container className="wrap">
      <div className="block-space" />
      <div className="page-nav-row">
        <Link
          className="page-nav-button"
          to="/#curious"
          aria-label="Back to curious works"
        >
          <FiArrowLeft />
        </Link>
      </div>

      <header>
        <HomeHeading>{data.name}</HomeHeading>
      </header>

      {data.architecture && (
        <ArchitectureBlock className={architectureLoaded ? "image-ready" : ""}>
          {!architectureLoaded && (
            <div className="media-skeleton" aria-hidden="true" />
          )}

          <img
            src={data.architecture}
            alt={`${data.name} architecture`}
            onLoad={() => setArchitectureLoaded(true)}
          />
        </ArchitectureBlock>
      )}

      <Article>
        <section>
          <span>Question</span>
          <h2>{data.question}</h2>
        </section>

        <section>
          <span>How I Found It</span>
          {data.how?.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </section>

        {data.findings?.length > 0 && (
          <section>
            <span>Important Findings</span>
            <ul>
              {data.findings.map((finding) => (
                <li key={finding}>{finding}</li>
              ))}
            </ul>
          </section>
        )}

        <section className="takeaway">
          <span>Takeaway</span>
          <p>{data.takeaway}</p>
        </section>

        {links.length > 0 && (
          <div className="links">
            {links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noreferrer"
              >
                {link.icon}
                {link.label}
                <FiExternalLink />
              </a>
            ))}
          </div>
        )}
      </Article>
    </Container>
  );
}

const Container = styled.div`
  position: relative;

  width: 100%;
  min-height: 100vh;
  padding-top: 20px;
  padding-bottom: 40px;
  font-family: "Poppins", sans-serif;

  .page-nav-row {
    width: 100%;
    max-width: 1120px;
    margin: 0 auto 48px;
  }

  .page-nav-button {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;

    border: 1px solid #3d3d3d;
    border-radius: 50%;
    background: rgba(14, 14, 14, 0.72);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);

    color: #ababab;
    text-decoration: none;

    transition:
      border-color 0.2s ease,
      color 0.2s ease,
      transform 0.2s ease;

    &:hover {
      border-color: #fff;
      color: #fff;
      transform: translateX(-3px);
    }
  }

  header {
    width: 100%;
    max-width: 1120px;
    margin: 0 auto;
  }

  @media (max-width: 760px) {
    .page-nav-row {
      margin-bottom: 34px;
    }
  }
`;

const ArchitectureBlock = styled.div`
  position: relative;
  width: 100%;
  max-width: 1120px;
  margin: 76px auto 0;
  min-height: clamp(240px, 42vw, 520px);
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;

  .media-skeleton {
    position: absolute;
    inset: 0;
    z-index: 1;
    overflow: hidden;
    background:
      linear-gradient(110deg, transparent 0%, rgba(255, 255, 255, 0.055) 50%, transparent 100%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.012));

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(
        100deg,
        transparent 0%,
        rgba(255, 255, 255, 0.075) 42%,
        transparent 84%
      );
      transform: translateX(-100%);
      animation: curiousMediaSkeleton 1.35s ease-in-out infinite;
    }
  }

  img {
    position: relative;
    z-index: 0;
    display: block;
    width: min(calc(100% - 48px), 960px);
    height: 100%;
    max-height: 460px;
    object-fit: contain;
    opacity: 0;
    user-select: none;
    pointer-events: none;
    transition: opacity 0.28s ease;
  }

  &.image-ready img {
    opacity: 1;
  }

  @keyframes curiousMediaSkeleton {
    100% {
      transform: translateX(100%);
    }
  }

  @media (max-width: 760px) {
    margin-top: 48px;
    min-height: clamp(220px, 64vw, 360px);

    img {
      width: calc(100% - 28px);
      max-height: 330px;
    }
  }
`;

const Article = styled.article`
  width: 100%;
  max-width: 760px;
  margin: 76px auto 0;

  section {
    padding: 38px 0;
    border-top: 1px solid #2b2b2b;
  }

  span {
    display: block;
    margin-bottom: 16px;
    color: #858585;
    font-size: 12px;
    font-weight: 300;
    letter-spacing: 1.3px;
    text-transform: uppercase;
  }

  h2 {
    margin: 0;
    color: #fff;
    font-size: clamp(20px, 2.4vw, 30px);
    font-weight: 400;
    line-height: 1.45;
  }

  p {
    margin: 0 0 16px;
    color: #ababab;
    font-size: 16px;
    font-weight: 300;
    line-height: 1.9;
  }

  ul {
    display: grid;
    gap: 12px;
    margin: 0;
    padding-left: 20px;
  }

  li {
    color: #ababab;
    font-size: 16px;
    font-weight: 300;
    line-height: 1.8;
  }

  .takeaway {
    p {
      margin: 0;
      color: #fff;
      font-size: clamp(19px, 2.3vw, 25px);
      line-height: 1.65;
    }
  }

  .links {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    padding-top: 28px;
    border-top: 1px solid #2b2b2b;

    a {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      min-height: 42px;
      padding: 0 14px;
      border: 1px solid #3d3d3d;
      border-radius: 8px;
      color: #ababab;
      background: rgb(14, 14, 14);
      font-size: 13px;
      font-weight: 300;
      text-decoration: none;
      transition:
        border-color 0.2s ease,
        color 0.2s ease;

      &:hover {
        border-color: #fff;
        color: #fff;
      }
    }
  }

  @media (max-width: 760px) {
    margin-top: 48px;

    section {
      padding: 30px 0;
    }
  }
`;
